import { EntityKind, buildBusinessErrorFromError, ContributionInformationSchema, ContributionInformationDepotContextSchema, ContributionInformationDepotContextPiecesSchema, } from '@mgdis/eu-domain';
class ContributionsRestAdapter {
    tenantId;
    restHttpAdapter;
    /**
     * API of contributions.
     */
    constructor({ tenantId, restHttpAdapter }) {
        this.tenantId = tenantId;
        this.restHttpAdapter = restHttpAdapter;
    }
    /**
     * Call API: Get a contribution from a demande.
     *
     * @param demandeHref - demande id
     * @param statut - contribution status
     * @returns contribution
     */
    async getContributionFromDemande(demandeHref, statut) {
        try {
            const res = await this.restHttpAdapter.get(`/referentiel-financement/api/tenants/${this.tenantId}/contributions?$filter=demande/href eq '${demandeHref}' and statut eq '${statut}'`);
            return { response: res.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Call API: Patch a contribution by its reference.
     *
     * @param reference - contribution reference
     * @param patch - patch
     * @returns contribution
     */
    async patchContributionByReference(reference, patch) {
        try {
            const res = await this.restHttpAdapter.patch(`/referentiel-financement/api/tenants/${this.tenantId}/contributions/${reference}`, patch);
            return { response: res.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Call API: Create a new contribution for avis de financement
     *
     * @param demandeReference - demande reference
     * @param tiersRef - tiers reference
     * @returns contribution
     */
    async createContributionAvisFinancement(demandeReference, tiersRef) {
        try {
            const res = await this.restHttpAdapter.post(`/referentiel-financement/api/tenants/${this.tenantId}/contributions/avis-financement/${demandeReference}/${tiersRef}`);
            return { response: res.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    async deleteContributionInformation(contributionInformationReference) {
        try {
            await this.restHttpAdapter.delete(`/referentiel-financement/api/tenants/${this.tenantId}/contributions-information/${contributionInformationReference}`);
            return { response: null };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    async listByDemandeFinancement(demandeFinancementReference) {
        try {
            const res = await this.restHttpAdapter.get(`/referentiel-financement/api/tenants/${this.tenantId}/demandes-financement/${demandeFinancementReference}/contributions-information`);
            return { response: res.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Create contribution information
     *
     * @param demandeId - Reference of demande financement linked to the contribution information
     * @param teleserviceContributionHref - Id of contribution Information teleservice
     */
    async createContributionInformation(demandeId, teleserviceContributionHref) {
        try {
            const data = {
                demandeHref: demandeId,
                teleserviceHref: teleserviceContributionHref,
            };
            const res = await this.restHttpAdapter.post(`/referentiel-financement/api/tenants/${this.tenantId}/contributions-information/`, data);
            const result = await ContributionInformationSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: buildBusinessErrorFromError(result.error, { entityKind: EntityKind.CONTRIBUTIONS }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error) };
        }
    }
    /**
     * Edit the specified contribution information while it is IN PROGRESS
     *
     * @param contributionInformationReference - Reference of contribution information to edit
     * @param data - Data to change
     */
    async editContributionInformation(contributionInformationReference, data) {
        try {
            const res = await this.restHttpAdapter.put(`/referentiel-financement/api/tenants/${this.tenantId}/contributions-information/${contributionInformationReference}`, data);
            const result = await ContributionInformationSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: buildBusinessErrorFromError(result.error, { entityKind: EntityKind.CONTRIBUTIONS }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Submit the specified contribution information
     *
     * @param contributionInformationReference - Reference of contribution information to submit
     * @param data - Data to send with transmission
     */
    async submitContributionInformation(contributionInformationReference, data) {
        try {
            const res = await this.restHttpAdapter.post(`/referentiel-financement/api/tenants/${this.tenantId}/contributions-information/${contributionInformationReference}/submit`, data);
            const result = await ContributionInformationSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: buildBusinessErrorFromError(result.error, { entityKind: EntityKind.CONTRIBUTIONS }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Fetch the specified contribution information
     *
     * @param contributionInformationReference - Reference of contribution information to fetch
     */
    async getContributionInformation(contributionInformationReference) {
        try {
            const res = await this.restHttpAdapter.get(`/referentiel-financement/api/tenants/${this.tenantId}/contributions-information/${contributionInformationReference}`);
            const result = await ContributionInformationSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: buildBusinessErrorFromError(result.error, { entityKind: EntityKind.CONTRIBUTIONS }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Fetch data needed for depot of contribution information. It includes teleservice, demande-financement, contribution information, pieces to display and mandatory pieces
     *
     * @param contributionInformationReference - Reference of contribution information to fetch data from
     */
    async getContributionInformationDepotContext(contributionInformationReference) {
        try {
            const res = await this.restHttpAdapter.get(`/referentiel-financement/api/tenants/${this.tenantId}/bff/contributions-information/${contributionInformationReference}/depot-context`);
            const result = await ContributionInformationDepotContextSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: buildBusinessErrorFromError(result.error, { entityKind: EntityKind.CONTRIBUTIONS }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
    /**
     * Compute pieces config for a specific contribution information. Includes conditionAffichage and conditionObligatoire
     *
     * @param contributionInformationReference - Reference of contribution information to fetch data from
     * @param currentInformations - Current informations in contribution information form
     */
    async computeContributionInformationPiecesConfig(contributionInformationReference, currentInformations) {
        try {
            const res = await this.restHttpAdapter.post(`/referentiel-financement/api/tenants/${this.tenantId}/bff/contributions-information/${contributionInformationReference}/depot-context/pieces`, currentInformations);
            const result = await ContributionInformationDepotContextPiecesSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: buildBusinessErrorFromError(result.error, { entityKind: EntityKind.CONTRIBUTIONS }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return { error: buildBusinessErrorFromError(error, { entityKind: EntityKind.CONTRIBUTIONS }) };
        }
    }
}
export { ContributionsRestAdapter };
//# sourceMappingURL=contributions-rest.adapter.js.map